import React, { useEffect, useMemo, useRef, useState } from "react";
import { optimizeImageFile } from "../../services/images/optimizeImage";
import BienvenidaText from "../BienvenidaText";
import { useAuth } from "../context/AuthContext";
import {
  analyzeImageLuminance,
  buildAdaptiveHeroPalette,
} from "./heroContrast";
import {
  RESTAURANT_PROFILE_UPDATED_EVENT,
  getRestaurantProfile,
  normalizeCoverDisplayMode,
  saveRestaurantProfile,
} from "../../services/local/restaurantProfile";
import {
  getBranchPublicProfile,
  updateBranchPublicProfile,
} from "../../services/supabase/companyAdminService";


const styles = {
  imageContainer: {
    width: "100%",
    height: "280px",
    border: "1px solid rgba(148, 163, 184, 0.26)",
    position: "relative",
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundColor: "#edf2f7",
  },
  stretchImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "fill",
    objectPosition: "center",
  },
  normalImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
  },
  centeredBackdrop: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    filter: "blur(24px)",
    transform: "scale(1.08)",
    opacity: 0.4,
  },
  centeredOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(237,242,247,0.18) 0%, rgba(237,242,247,0.34) 100%)",
  },
  centeredForeground: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
  },
  textWrap: {
    position: "relative",
    zIndex: 1,
  },
  controls: {
    position: "absolute",
    bottom: "14px",
    left: "14px",
    zIndex: 2,
  },
  uploadButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "44px",
    height: "44px",
    borderRadius: "999px",
    backgroundColor: "rgba(255,255,255,0.92)",
    color: "#0f172a",
    border: "1px solid rgba(226, 232, 240, 0.9)",
    boxShadow: "0 10px 30px rgba(15,23,42,0.15)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "24px",
    backdropFilter: "blur(6px)",
  },
  input: {
    display: "none",
  },
};

const getLegacyHeroStorageKey = (restaurantId) =>
  `mozzoapp:hero-image:${restaurantId || "default"}`;

const clearStoredHeroImage = (storageKey) => {
  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    // Ignoramos limpieza local para no frenar la UI.
  }
};

const resizeImageFile = (file) => optimizeImageFile(file, { profile: "cover" });

function Image({ children, uploadMode = "always" }) {
  const { currentRestaurant, isCreatorMode, isSupabaseConfigured } = useAuth();
  const [imageUrl, setImageUrl] = useState("");
  const [processingImage, setProcessingImage] = useState(false);
  const imageRequestRef = useRef(0);
  useEffect(() => {
    setProcessingImage(false);
    return () => { imageRequestRef.current += 1; };
  }, [currentRestaurant?.id]);
  const [coverDisplayMode, setCoverDisplayMode] = useState("stretch");
  const [heroPalette, setHeroPalette] = useState(() =>
    buildAdaptiveHeroPalette({
      backgroundLuminance: 0.55,
      preferredTextColor: "#f8fafc",
    })
  );

  const storageKey = useMemo(() => {
    if (currentRestaurant?.id) {
      return getLegacyHeroStorageKey(currentRestaurant.id);
    }

    return getLegacyHeroStorageKey(isCreatorMode ? "creator" : "guest");
  }, [currentRestaurant?.id, isCreatorMode]);

  useEffect(() => {
    clearStoredHeroImage(storageKey);
    const profile = getRestaurantProfile(currentRestaurant);
    const profileImage = profile.imagenPortada || profile.coverUrl || "";
    setImageUrl(profileImage || currentRestaurant?.coverUrl || "");
    setCoverDisplayMode(normalizeCoverDisplayMode(profile.coverDisplayMode));
  }, [currentRestaurant, isCreatorMode, isSupabaseConfigured, storageKey]);

  useEffect(() => {
    const syncFromProfile = (profileOverride) => {
      const profile = profileOverride || getRestaurantProfile(currentRestaurant);
      setCoverDisplayMode(normalizeCoverDisplayMode(profile?.coverDisplayMode));

      const profileImage = profile?.imagenPortada || profile?.coverUrl || "";
      setImageUrl(profileImage || currentRestaurant?.coverUrl || "");
    };

    const handleProfileUpdate = (event) => {
      if (!event?.detail) {
        syncFromProfile();
        return;
      }

      const updatedRestaurantId = event.detail.restaurantId || "default";
      const currentRestaurantId = currentRestaurant?.id || "default";

      if (updatedRestaurantId !== currentRestaurantId) {
        return;
      }

      syncFromProfile(event.detail.profile);
    };

    const handleStorage = () => {
      syncFromProfile();
    };

    window.addEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(RESTAURANT_PROFILE_UPDATED_EVENT, handleProfileUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentRestaurant, currentRestaurant?.coverUrl]);

  useEffect(() => {
    let cancelled = false;

    if (!imageUrl) {
      setHeroPalette(
        buildAdaptiveHeroPalette({
          backgroundLuminance: 0.55,
          preferredTextColor: "#f8fafc",
        })
      );
      return () => {
        cancelled = true;
      };
    }

    analyzeImageLuminance(imageUrl)
      .then((backgroundLuminance) => {
        if (!cancelled) {
          setHeroPalette(
            buildAdaptiveHeroPalette({
              backgroundLuminance,
              preferredTextColor: "#f8fafc",
            })
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHeroPalette(
            buildAdaptiveHeroPalette({
              backgroundLuminance: 0.55,
              preferredTextColor: "#f8fafc",
            })
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || processingImage) {
      return;
    }

    const request = ++imageRequestRef.current;
    setProcessingImage(true);
    try {
      const nextImage = await resizeImageFile(file);
      if (request !== imageRequestRef.current) return;
      clearStoredHeroImage(storageKey);
      setImageUrl(nextImage);

      const nextProfile = {
        ...getRestaurantProfile(currentRestaurant),
        imagenPortada: nextImage,
        coverUrl: nextImage,
      };

      if (!isCreatorMode && isSupabaseConfigured && currentRestaurant?.id) {
        const remoteProfile = await getBranchPublicProfile(currentRestaurant.id);
        const syncedProfile = await updateBranchPublicProfile(currentRestaurant.id, {
          ...remoteProfile,
          coverUrl: nextImage,
        });
        saveRestaurantProfile(currentRestaurant, {
          ...nextProfile,
          imagenPortada: syncedProfile.coverUrl || nextImage,
          coverUrl: syncedProfile.coverUrl || nextImage,
        });
      } else {
        saveRestaurantProfile(currentRestaurant, nextProfile);
      }
    } catch (error) {
      alert(error.message || "No se pudo guardar la imagen del restaurante.");
    } finally {
      if (request === imageRequestRef.current) setProcessingImage(false);
      event.target.value = "";
    }
  };

  const showUploadButton =
    uploadMode === "always" || (uploadMode === "if-empty" && !imageUrl);

  return (
    <div style={styles.imageContainer}>
      {imageUrl && coverDisplayMode === "centered" ? (
        <>
          <img src={imageUrl} alt="" aria-hidden="true" style={styles.centeredBackdrop} />
          <div style={styles.centeredOverlay} />
          <img
            src={imageUrl}
            alt={`Portada de ${currentRestaurant?.name || "MozzoApp"}`}
            style={styles.centeredForeground}
          />
        </>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={`Portada de ${currentRestaurant?.name || "MozzoApp"}`}
          style={coverDisplayMode === "normal" ? styles.normalImage : styles.stretchImage}
        />
      ) : null}

      {!imageUrl && (
        <div style={styles.textWrap}>
          {children || <BienvenidaText text="Bienvenidos a MozzoApp" />}
        </div>
      )}

      {showUploadButton && (
        <>
          <div style={styles.controls}>
            <label
              htmlFor="restaurant-cover-input"
              style={{
                ...styles.uploadButton,
                backgroundColor: imageUrl
                  ? heroPalette.usesDarkText
                    ? "rgba(15,23,42,0.82)"
                    : "rgba(255,255,255,0.92)"
                  : styles.uploadButton.backgroundColor,
                color: imageUrl
                  ? heroPalette.usesDarkText
                    ? "#f8fafc"
                    : "#0f172a"
                  : styles.uploadButton.color,
                borderColor: imageUrl
                  ? heroPalette.usesDarkText
                    ? "rgba(15,23,42,0.8)"
                    : "rgba(226,232,240,0.92)"
                  : "rgba(226,232,240,0.9)",
                boxShadow: imageUrl
                  ? heroPalette.usesDarkText
                    ? "0 12px 30px rgba(15,23,42,0.28)"
                    : "0 10px 30px rgba(15,23,42,0.15)"
                  : styles.uploadButton.boxShadow,
              }}
            >
              <span style={{ lineHeight: 1 }} aria-label={processingImage ? "Optimizando imagen" : "Cambiar portada"}>{processingImage ? "..." : "+"}</span>
            </label>
          </div>
          <input
            type="file"
            style={styles.input}
            onChange={handleImageChange}
            disabled={processingImage}
            accept="image/*"
            id="restaurant-cover-input"
          />
        </>
      )}
    </div>
  );
}

export default Image;
